// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// material ui
import { Box, Menu, MenuItem, Typography } from '@mui/material';

// api
import {
  type GetAlarmConfigurationsApiArg,
  useGetAlarmConfigurationsQuery,
} from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';
import {
  type GetV2AlarmTypesApiArg,
  useGetV2AlarmTypesQuery,
} from '@/api/generated/recalc/endpoints/v2/alarmTypes';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { SubmitButton } from '@/components/ui/button/SubmitButton';

// feature-components
import AlarmConfigurationDataGrid from '@/features/alarm-hub/components/alarm-configuration/AlarmConfigurationDataGrid';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';

export const Route = createFileRoute('/$accountId/_user/alarms/configuration/')(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  const { t } = useTranslation('alarms');
  const navigate = Route.useNavigate();

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );

  const { accountId } = useParams({ strict: false });
  const { isAdmin } = useSessionAuth();

  const alarmTypesQueryArgs: GetV2AlarmTypesApiArg = {
    orderBy: 'name',
    orderDirection: 'asc',
  };

  const {
    data: alarmTypes = [],
    isLoading,
    isError,
  } = useGetV2AlarmTypesQuery(alarmTypesQueryArgs);

  const alarmConfigurationsQueryArgs: GetAlarmConfigurationsApiArg = {
    accountIds: accountId ? [Number(accountId)] : undefined,
  };
  const { data: alarmConfigurations = [] } = useGetAlarmConfigurationsQuery(
    alarmConfigurationsQueryArgs,
    {
      skip: alarmTypes.length === 0,
    },
  );

  const userAlarmConfigurations = alarmConfigurations.filter(
    (config) => config.alarmType === 'UsageOverTime',
  );

  const configurableAlarmTypes = alarmTypes.filter(
    (alarmType) =>
      alarmType.configurationSchema !== null && alarmType.alarmTypeId != null,
  );

  const userConfigurableAlarmTypes = configurableAlarmTypes.filter(
    (alarmType) => ['Usage over time alarm'].includes(alarmType.name ?? ''),
  );

  const finalConfigurableAlarmTypes = isAdmin
    ? configurableAlarmTypes
    : userConfigurableAlarmTypes;

  const isCreateMenuOpen = Boolean(menuAnchorEl);

  const handleOpenCreateMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseCreateMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader
        title={t('alarmConfiguration.title', {
          defaultValue: 'Alarm Configurations',
        })}
        disableArrow
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SubmitButton
            id="alarm-configuration-create-menu-button"
            aria-controls={
              isCreateMenuOpen ? 'alarm-configuration-create-menu' : undefined
            }
            aria-haspopup="true"
            aria-expanded={isCreateMenuOpen ? 'true' : undefined}
            type="button"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleOpenCreateMenu}
            disabled={
              isLoading || isError || finalConfigurableAlarmTypes.length === 0
            }
          >
            {t('toolbar.new_configuration', {
              defaultValue: 'New Configuration',
            })}
          </SubmitButton>

          <Menu
            id="alarm-configuration-create-menu"
            anchorEl={menuAnchorEl}
            open={isCreateMenuOpen}
            onClose={handleCloseCreateMenu}
          >
            {finalConfigurableAlarmTypes.map((alarmType) => (
              <Box key={alarmType.alarmTypeId ?? alarmType.name}>
                <MenuItem
                  onClick={() => {
                    handleCloseCreateMenu();
                    navigate({
                      to: '/$accountId/alarms/configuration/create/$alarmTypeId',
                      params: {
                        accountId: String(accountId),
                        alarmTypeId: String(alarmType.alarmTypeId),
                      } as never,
                    });
                  }}
                  sx={{ mx: 1 }}
                  disableRipple
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {alarmType.name
                        ? t(`alarm_types.${alarmType.name}`, {
                            defaultValue: alarmType.name,
                          })
                        : '-'}
                    </Typography>
                    {alarmType.description && (
                      <Typography variant="caption" color="text.secondary">
                        {alarmType.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              </Box>
            ))}
          </Menu>
        </Box>
      </PageHeader>

      <AlarmConfigurationDataGrid
        alarmConfigurations={
          isAdmin ? alarmConfigurations : userAlarmConfigurations
        }
        accountId={Number(accountId)}
      />
    </PageContainer>
  );
}
